<script lang="ts">
    import { STATUS } from "@/constants/statuses";
    import { ModalManager } from "@/modal-manager";
    import { AddToRegistryModalWidgetDefinition } from "@apps/gift_registry/definitions";
    import GiftRegistryQuery from "@apps/gift_registry/queries/GiftRegistryQuery";
    import GiftRegistryService from "@apps/gift_registry/services/GiftRegistryService";
    import { addToRegistryModalToken } from "@apps/gift_registry/tokens";
    import { ProductIdManagerInterface } from "@lib/ProductIdManager/ProductIdManagerInterface";
    import { ProductVariantIdManagerInterface } from "@lib/ProductVariantIdManager/ProductVariantIdManagerInterface";
    import { AuthenticationInfoProviderInterface } from "@modules/authentication/AuthenticationInfoProvider/AuthenticationInfoProviderInterface";
    import { ROLE } from "@modules/authentication/constants";
    import { DynamicImportManager } from "@modules/dynamic_import/DynamicImportManager";
    import { ProductsServiceInterface } from "@modules/products/services/ProductsServiceInterface";
    import CN from "classnames";
    import { lastValueFrom, map } from "rxjs";
    import { onMount } from "svelte";

    import BaseButton from "@components/Buttons/BaseButton.svelte";
    import Button from "@components/Buttons/Button.svelte";
    import T from "@components/T.svelte";

    export let productsService: ProductsServiceInterface;
    export let giftRegistryService: GiftRegistryService;
    export let giftRegistryQuery: GiftRegistryQuery;
    export let modalManager: ModalManager;
    export let productIdManager: ProductIdManagerInterface;
    export let productVariantIdManager: ProductVariantIdManagerInterface;
    export let buttonClass: string;
    export let dynamicImportManager: DynamicImportManager;
    export let authenticationInfoProvider: AuthenticationInfoProviderInterface;
    export let afterWidgetInject: () => void;

    const status$ = giftRegistryQuery.status$;

    const productId$ = productIdManager.productId$;
    const variantId$ = productVariantIdManager.variantId$;
    const isCustomer$ = authenticationInfoProvider
        .selectRole()
        .pipe(map((role) => role === ROLE.Customer));

    const isVariantExistsInRegistries$ =
        giftRegistryQuery.selectVariantExistsInRegistries(
            $productId$,
            $variantId$
        );

    const DynamicButton = buttonClass ? BaseButton : Button;

    const handleOpenRegistryModal = async () => {
        await dynamicImportManager.fetch({
            definition: AddToRegistryModalWidgetDefinition,
        });
        // Ensure the modal widget initializes asynchronously before opening it
        setTimeout(() => {
            modalManager.openModal(addToRegistryModalToken, undefined, {
                productId: $productId$,
                variantId: $variantId$,
            });
        }, 0);
    };

    const handleClick = () => {
        handleOpenRegistryModal();
    };

    onMount(() => {
        const productSubscription = productId$.subscribe((productId) => {
            if (productId) productsService.addProductForFetching(productId);
        });

        if ($isCustomer$) {
            lastValueFrom(giftRegistryService.getGiftRegistriesWithItems());
        }

        afterWidgetInject();

        return () => {
            productSubscription.unsubscribe();
        };
    });
</script>

<div class="gw-gr-add-to-registry-product-page-widget">
    <DynamicButton
        type="button"
        on:click={handleClick}
        variant="outlined"
        disabled={[STATUS.LOADING, STATUS.INITIAL_LOADING].includes($status$)}
        class={CN(buttonClass)}
        withoutReset
        block
    >
        {#if $isVariantExistsInRegistries$}
            <T
                namespace="giftRegistry.critical.AddToRegistryProductPageWidget"
                translationKey="added_to_registry"
            />
        {:else}
            <T
                namespace="giftRegistry.critical.AddToRegistryProductPageWidget"
                translationKey="add_to_registry"
            />
        {/if}
    </DynamicButton>
</div>

<style lang="postcss">
    .gw-gr-add-to-registry-product-page-widget {
        --gw-button__main-color: var(--gw-gr-theme-primaryColor);

        & :global(.gw-btn.gw-button.--outlined) {
            background-color: transparent;
            box-shadow: 0 0 0 1px var(--gw-button__main-color);
            border-radius: 2px;
            color: var(--gw-gr-theme-fontColor);
            min-height: 40px;
            transition: all 0.3s;
            border: 0;
        }
    }
</style>
